import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NotificationsService } from '../notifications/notifications.service';
import { UserManagementService } from '../user-management/user-management.service';
import { merge, Subscription } from 'rxjs';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import {
  NavbarMenuItem,
  NAVBAR_NOTIFICATION_PERMISSION,
} from './navbar-config';
import {
  PermissionCheck,
  PermissionService,
} from '../report-system/core/services/permission.service';

@Component({
  selector: 'app-shared-navbar',
  templateUrl: './shared-navbar.component.html',
  styleUrls: ['./shared-navbar.component.css'],
  standalone: false,
})
export class SharedNavbarComponent implements OnInit, OnDestroy {
  @ViewChild('notificationListComponent') notificationListComponent: any;

  @Input() brandRoute: any[] | string = '/';
  @Input() siteName = '';
  @Input() showSiteName = true;
  @Input() menuItems: NavbarMenuItem[] = [];
  @Input() profileMenuItems: NavbarMenuItem[] = [];
  @Input() avatarSrc: any = './assets/images/avathar.jpg';
  @Input() avatarAlt = 'User avatar';
  @Input() navbarClass = 'navbar navbar-expand-lg bg-custom boxshadow fixed-top';
  @Input() showNotifications = false;
  @Input() notificationPermission: PermissionCheck | null =
    NAVBAR_NOTIFICATION_PERMISSION;

  @Output() itemSelected = new EventEmitter<string>();

  public isMenuCollapsed = true;
  unreadNotificationCount = 0;
  isNotificationPanelOpen = false;
  isAnyMenuOpen = false;
  private readonly defaultSubmenuWidth = 240;

  private subscriptions: Subscription[] = [];
  private openDropdowns = new Map<NgbDropdown, HTMLElement>();
  private submenuPlacements = new Map<string, 'right' | 'left'>();

  constructor(
    private notificationService: NotificationsService,
    private cdr: ChangeDetectorRef,
    private permissionService: PermissionService,
    // Read here rather than taken as an @Input: five different navbars host
    // this component, and one of them forgetting to pass it would leave the
    // profile drawer silently blank.
    private userManagementService: UserManagementService
  ) {}

  ngOnInit(): void {
    const permissionSub = merge(
      this.permissionService.permissions$,
      this.permissionService.newAuthPermissions$
    ).subscribe(() => {
      this.cdr.detectChanges();
    });
    this.subscriptions.push(permissionSub);

    if (!this.showNotifications) {
      return;
    }

    const unreadSub = this.notificationService.unreadCount$.subscribe((count) => {
      this.unreadNotificationCount = count;
      this.cdr.detectChanges();
    });
    this.subscriptions.push(unreadSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    document.body.style.overflow = '';
  }

  get visibleMenuItems(): NavbarMenuItem[] {
    return this.menuItems.filter((item) => this.isVisible(item));
  }

  get visibleProfileMenuItems(): NavbarMenuItem[] {
    return this.profileMenuItems.filter((item) => this.isVisible(item));
  }

  get shouldShowNotifications(): boolean {
    if (!this.showNotifications) {
      return false;
    }

    if (!this.notificationPermission) {
      return true;
    }

    return this.permissionService.hasPermission(this.notificationPermission);
  }

  isVisible(item: NavbarMenuItem): boolean {
    return item.visible !== false && this.hasItemPermission(item);
  }

  // ── Phone bottom bar ─────────────────────────────────────────────────
  // Replaces the hamburger below 768px. It reads the same permission-
  // filtered menuItems the desktop bar uses, so a role that loses a menu
  // loses the tab with it — nothing is duplicated or hardcoded here.

  /**
   * Slots in the pill before the overflow button takes over.
   *
   * Six covers every top-level menu a role currently has, so More does not
   * appear at all — seven 44px targets plus padding come to ~322px, which
   * fits the 375px narrowest phone. Raising this further would overflow
   * there; the pill scrolls as a backstop, but the icons stop being
   * reachable at a glance.
   */
  private static readonly MOBILE_TAB_LIMIT = 6;

  /** Top-level item whose children are currently shown in the sheet. */
  activeSheetItem: NavbarMenuItem | null = null;
  showOverflowSheet = false;

  /**
   * The phone menu, with the two report branches collapsed into one tab.
   *
   * "REPORTS" and "SPECIAL REPORTS" are separate top-level menus on desktop,
   * where there is room for both. On a seven-slot pill they read as two
   * near-identical icons, so they become one tab that asks which you want.
   * Desktop is untouched — visibleMenuItems is what the desktop bar uses.
   */
  private mobileMenuCache: {
    source: NavbarMenuItem[];
    value: NavbarMenuItem[];
  } | null = null;

  get mobileMenuItems(): NavbarMenuItem[] {
    const items = this.visibleMenuItems;

    // Memoised on the source contents. Returning a freshly built array here
    // would give *ngFor new object identities every change-detection pass,
    // so it would destroy and recreate every tab element continuously — and
    // a real tap (touchstart -> touchend -> click) would land on an element
    // that no longer exists. A synthetic .click() is synchronous and hides
    // this completely.
    const cache = this.mobileMenuCache;
    if (
      cache &&
      cache.source.length === items.length &&
      cache.source.every((item, i) => item === items[i])
    ) {
      return cache.value;
    }
    const isSpecial = (i: NavbarMenuItem) =>
      /special/i.test(i.label || "") && /report/i.test(i.label || "");
    const isPlainReports = (i: NavbarMenuItem) =>
      /report/i.test(i.label || "") && !isSpecial(i);

    const reports = items.find(isPlainReports);
    const special = items.find(isSpecial);

    // nothing to merge unless both are present for this role
    if (!reports || !special) {
      this.mobileMenuCache = { source: [...items], value: items };
      return items;
    }

    const merged: NavbarMenuItem = {
      label: "Reports",
      drillOnly: true,
      children: [
        { ...reports, label: "Single Report" },
        { ...special, label: "Special Report" },
      ],
    };

    const out: NavbarMenuItem[] = [];
    items.forEach((item) => {
      if (item === reports) {
        out.push(merged);
        return;
      }
      if (item === special) {
        return; // folded into `merged` above
      }
      out.push(item);
    });

    this.mobileMenuCache = { source: [...items], value: out };
    return out;
  }

  /** Keyed on label so a re-evaluated menu does not rebuild the DOM. */
  trackTab = (_: number, item: NavbarMenuItem) => item.label;

  get mobileTabItems(): NavbarMenuItem[] {
    return this.mobileMenuItems.slice(
      0,
      SharedNavbarComponent.MOBILE_TAB_LIMIT,
    );
  }

  get mobileOverflowItems(): NavbarMenuItem[] {
    return this.mobileMenuItems.slice(SharedNavbarComponent.MOBILE_TAB_LIMIT);
  }

  get hasMobileOverflow(): boolean {
    return this.mobileOverflowItems.length > 0;
  }

  get isMobileSheetOpen(): boolean {
    return this.showOverflowSheet || !!this.activeSheetItem;
  }

  /**
   * Font Awesome 4 glyph for a menu label. Matching is on substrings so a
   * renamed or re-cased label still resolves; anything unrecognised falls
   * back to a neutral mark rather than rendering an empty box.
   */
  tabIcon(label: string): string {
    const l = (label || "").toLowerCase();
    if (l.includes("alert")) return "fa-exclamation-triangle";
    if (l.includes("upload")) return "fa-upload";
    if (l.includes("setting") || l.includes("customization")) return "fa-cog";
    if (l.includes("pfd") || l.includes("diagram")) return "fa-sitemap";
    if (l.includes("special")) return "fa-star-o";
    if (l.includes("report")) return "fa-bar-chart";
    if (l.includes("equipment")) return "fa-wrench";
    if (l.includes("user")) return "fa-users";
    return "fa-circle-o";
  }

  // ── Profile drawer (phones) ──────────────────────────────────────────
  // Replaces the dropdown below 768px: a dropdown anchored to a 30px avatar
  // in the corner is awkward to hit and cannot show the account details.

  showProfileDrawer = false;

  get currentUser(): any {
    return this.userManagementService.getUserDetail() || {};
  }

  get profileDisplayName(): string {
    const u = this.currentUser;
    const full = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
    return full || u.name || u.username || "User";
  }

  get profileSubtitle(): string {
    const u = this.currentUser;
    return u.role_name || u.user_role || "";
  }

  /** Phone and email sit in the header, under the name. */
  get profilePhone(): string {
    const u = this.currentUser;
    return u.phone || u.phone_number || u.mobile || "";
  }

  get profileEmail(): string {
    return this.currentUser.email || "";
  }

  /** Leading glyph for a profile menu row. */
  profileIcon(item: NavbarMenuItem): string {
    if (item.actionId === "logout") {
      return "fa-power-off";
    }
    const l = (item.label || "").toLowerCase();
    if (l.includes("privacy") || l.includes("data")) return "fa-shield";
    if (l.includes("audit") || l.includes("log")) return "fa-file-text-o";
    if (l.includes("history")) return "fa-history";
    return "fa-user-o";
  }

  openProfileDrawer(): void {
    this.closeMobileSheet();
    this.showProfileDrawer = true;
  }

  closeProfileDrawer(): void {
    this.showProfileDrawer = false;
  }

  /** A profile menu row inside the drawer — act, then dismiss. */
  onProfileDrawerItem(item: NavbarMenuItem, event?: Event): void {
    this.onItemClick(item, event);
    if (!item.disabled) {
      this.closeProfileDrawer();
    }
  }

  /**
   * Artwork for a tab, where a supplied image says it better than a glyph.
   * Returns null when the font icon from tabIcon() should be used instead.
   */
  tabImage(label: string): string | null {
    const l = (label || "").toLowerCase();
    if (l.includes("plant")) {
      // "PLANT SETTINGS" is the facility, not app preferences — a cog reads
      // as the latter.
      return "./assets/images/industry.png";
    }
    return null;
  }

  /** A tab press: drill into children if there are any, else just navigate. */
  onMobileTab(item: NavbarMenuItem, event?: Event): void {
    if (this.isSubmenu(item)) {
      event?.preventDefault();
      this.showOverflowSheet = false;
      this.activeSheetItem = item;
      return;
    }
    this.closeMobileSheet();
    this.onItemClick(item, event);
  }

  openOverflowSheet(): void {
    this.activeSheetItem = null;
    this.showOverflowSheet = true;
  }

  closeMobileSheet(): void {
    this.activeSheetItem = null;
    this.showOverflowSheet = false;
  }

  /** Leaf tapped inside the sheet — navigate, then dismiss. */
  onSheetLeafClick(item: NavbarMenuItem, event?: Event): void {
    // Web-only rows are inert here. The sheet is reachable only from the
    // phone bottom bar, so this cannot affect the desktop dropdowns, which
    // still route through onItemClick untouched.
    if (item.webOnly) {
      event?.preventDefault();
      return;
    }
    this.onItemClick(item, event);
    if (!item.disabled) {
      this.closeMobileSheet();
    }
  }

  private sheetGroupsCache: {
    source: NavbarMenuItem | null;
    value: { label: string | null; items: NavbarMenuItem[] }[];
  } | null = null;

  /**
   * Flattens a menu branch into at most two levels for the sheet: direct
   * leaves first under no heading, then each sub-branch as its own group.
   * The desktop menu nests three deep, which a phone should not reproduce.
   *
   * Memoised on the branch identity. This is called from the template, so
   * without the cache it would build new group and row objects on every
   * change-detection pass; *ngFor would then destroy and recreate every
   * row continuously, and a real tap (touchstart -> touchend -> click)
   * would land on an element that no longer exists. A synthetic .click()
   * is synchronous and hides this, so verify with real TouchEvents.
   */
  sheetGroups(
    item: NavbarMenuItem | null,
  ): { label: string | null; items: NavbarMenuItem[] }[] {
    const cache = this.sheetGroupsCache;
    if (cache && cache.source === item) {
      return cache.value;
    }

    if (!item?.children || item.drillOnly) {
      // drillOnly branches render as rows instead — see the template
      this.sheetGroupsCache = { source: item, value: [] };
      return this.sheetGroupsCache.value;
    }

    const visible = item.children.filter((c) => this.isVisible(c));
    const leaves = visible.filter((c) => !this.isSubmenu(c));
    const branches = visible.filter((c) => this.isSubmenu(c));

    const groups: { label: string | null; items: NavbarMenuItem[] }[] = [];
    if (leaves.length) {
      groups.push({ label: null, items: leaves });
    }
    branches.forEach((b) =>
      groups.push({
        label: b.label,
        items: (b.children || []).filter((c) => this.isVisible(c)),
      }),
    );
    this.sheetGroupsCache = { source: item, value: groups };
    return groups;
  }

  /** *ngFor identity for sheet groups and their rows — see sheetGroups(). */
  trackSheetGroup = (_: number, group: { label: string | null }) =>
    group.label ?? "";

  trackSheetRow = (_: number, item: NavbarMenuItem) =>
    item.label ?? item.route ?? "";

  isSubmenu(item: NavbarMenuItem): boolean {
    return !!item.children?.some((child) => this.isVisible(child));
  }

  onItemClick(item: NavbarMenuItem, event?: Event): void {
    if (item.disabled) {
      event?.preventDefault();
      return;
    }

    if (item.actionId) {
      event?.preventDefault();
      this.itemSelected.emit(item.actionId);
    }

    this.closeAllDropdowns();
  }

  openDropdownOnHover(
    dropdown: NgbDropdown,
    event: MouseEvent,
    item?: NavbarMenuItem,
    isSubmenu = false
  ): void {
    const host = event.currentTarget as HTMLElement | null;
    if (!host) {
      return;
    }

    if (item && isSubmenu) {
      this.submenuPlacements.set(
        this.getItemKey(item),
        this.resolveSubmenuPlacement(host)
      );
    }

    this.closeUnrelatedDropdowns(host);
    dropdown.open();
    this.openDropdowns.set(dropdown, host);
    this.syncMenuBackdrop();
  }

  closeDropdownOnLeave(dropdown: NgbDropdown, event: MouseEvent): void {
    const host = this.openDropdowns.get(dropdown) ?? (event.currentTarget as HTMLElement | null);
    const nextTarget = event.relatedTarget as Node | null;

    if (host && nextTarget && host.contains(nextTarget)) {
      return;
    }

    this.closeDropdownBranch(host, dropdown);
  }

  onDropdownOpenChange(dropdown: NgbDropdown, isOpen: boolean): void {
    const host = this.openDropdowns.get(dropdown);

    if (isOpen && host) {
      this.openDropdowns.set(dropdown, host);
    } else {
      this.openDropdowns.delete(dropdown);
    }

    this.syncMenuBackdrop();
  }

  closeAllDropdowns(): void {
    this.openDropdowns.forEach((_, dropdown) => dropdown.close());
    this.openDropdowns.clear();
    this.syncMenuBackdrop();
  }

  private closeUnrelatedDropdowns(currentHost: HTMLElement): void {
    Array.from(this.openDropdowns.entries()).forEach(([dropdown, host]) => {
      const isRelated =
        host === currentHost ||
        host.contains(currentHost) ||
        currentHost.contains(host);

      if (!isRelated) {
        dropdown.close();
        this.openDropdowns.delete(dropdown);
      }
    });
  }

  private closeDropdownBranch(host: HTMLElement | null, fallbackDropdown?: NgbDropdown): void {
    if (!host) {
      if (fallbackDropdown) {
        fallbackDropdown.close();
        this.openDropdowns.delete(fallbackDropdown);
        this.syncMenuBackdrop();
      }
      return;
    }

    Array.from(this.openDropdowns.entries()).forEach(([dropdown, dropdownHost]) => {
      if (dropdownHost === host || host.contains(dropdownHost)) {
        dropdown.close();
        this.openDropdowns.delete(dropdown);
      }
    });

    this.syncMenuBackdrop();
  }

  private syncMenuBackdrop(): void {
    this.isAnyMenuOpen = this.openDropdowns.size > 0;
  }

  getDropdownPlacement(
    item: NavbarMenuItem,
    isSubmenu = false
  ): string {
    if (!isSubmenu) {
      return item.placement || 'bottom-left';
    }

    return this.submenuPlacements.get(this.getItemKey(item)) || item.placement || 'right';
  }

  isSubmenuOpenLeft(item: NavbarMenuItem): boolean {
    return this.getDropdownPlacement(item, true) === 'left';
  }

  private hasItemPermission(item: NavbarMenuItem): boolean {
    if (!item.permission) {
      return true;
    }

    if (Array.isArray(item.permission)) {
      if (!item.permission.length) {
        return false;
      }

      return item.permissionMode === 'all'
        ? this.permissionService.hasAllPermissions(item.permission)
        : this.permissionService.hasAnyPermission(item.permission);
    }

    return this.permissionService.hasPermission(item.permission);
  }

  private resolveSubmenuPlacement(host: HTMLElement): 'right' | 'left' {
    const hostRect = host.getBoundingClientRect();
    const availableRight = window.innerWidth - hostRect.right;
    const availableLeft = hostRect.left;

    if (
      availableRight < this.defaultSubmenuWidth &&
      availableLeft > availableRight
    ) {
      return 'left';
    }

    return 'right';
  }

  private getItemKey(item: NavbarMenuItem): string {
    return item.id || item.label;
  }

  toggleNotificationPanel(): void {
    this.isNotificationPanelOpen = !this.isNotificationPanelOpen;
    document.body.style.overflow = this.isNotificationPanelOpen ? 'hidden' : '';
  }

  closeNotificationPanel(): void {
    this.isNotificationPanelOpen = false;
    document.body.style.overflow = '';
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('notification-backdrop')) {
      this.closeNotificationPanel();
    }
  }

  get isDarkMode(): boolean {
    return this.notificationListComponent?.isDarkMode ?? false;
  }

  onThemeToggle(): void {
    if (!this.notificationListComponent) {
      return;
    }

    this.notificationListComponent.toggleTheme();
    this.cdr.detectChanges();
  }
}
